/**
 * `gamification-core` — WORKFORCE domain CONFIG of Jornada.
 *
 * PERSONAL PRODUCTIVITY gamification, strictly OPT-IN (default OFF)
 * and NEVER punitive nor comparative among employees. In a workforce context,
 * gamification is sensitive: workers activate it for THEMSEMSELVES, celebrating
 * their focus / productive activity milestones as engagement, and it is NEVER used as
 * surveillance or ranking. Private leagues by default (§leagues) — this app NEVER
 * exposes comparisons between individuals.
 *
 * Only PRODUCTIVE activity adds XP; distracting/neutral activity does NOT subtract (not
 * punitive). Gamification DERIVES from the minimized telemetry already synchronized
 * (`usage_event`: category + active seconds, NEVER content) — see
 * `derive.ts`. It is a pure, recomputable projection: it adds no tables and does not mutate the
 * hash-chain. The engine is agnostic; here we only declare the rules.
 */
import { counterAtLeast, streakAtLeast } from "./badges";
import type { GamificationConfig } from "./engine";

/** Domain event types (derived from minimized telemetry). */
export const JORNADA_EVENTS = {
  /** A FOCUS block: a stretch of productive use above the threshold. */
  focusBlock: "focus.block",
} as const;

/** Streak line: days with at least one focus block (personal consistency). */
export const FOCUS_STREAK_LINE = "focus";

/** Minimum threshold (active seconds) for a stretch to count as a focus block. */
export const MIN_FOCUS_SECS = 300;

export const jornadaGamificationConfig: GamificationConfig = {
  xp: {
    // Focus milestone: fixed XP per block of productive work (never by raw
    // duration amount → does not incentivize "working more hours", only consistency).
    [JORNADA_EVENTS.focusBlock]: 10,
  },
  streakLines: {
    [JORNADA_EVENTS.focusBlock]: [FOCUS_STREAK_LINE],
  },
  counters: {
    [JORNADA_EVENTS.focusBlock]: ["focus_blocks"],
  },
  badges: [
    {
      id: "first-focus",
      title: "First focus",
      description: "Log your first focus block.",
      when: counterAtLeast("focus_blocks", 1),
    },
    {
      id: "focused-ten",
      title: "Focused ten",
      description: "Log ten focus blocks.",
      when: counterAtLeast("focus_blocks", 10),
    },
    {
      id: "steady-week",
      title: "Steady week",
      description: "Keep your focus streak for seven days in a row.",
      when: streakAtLeast(FOCUS_STREAK_LINE, 7),
      bonusXp: 50,
    },
    {
      id: "deep-worker",
      title: "Deep worker",
      description: "Log fifty focus blocks.",
      when: counterAtLeast("focus_blocks", 50),
    },
  ],
  quests: [
    {
      id: "daily-focus",
      title: "Three focus blocks today",
      period: "daily",
      goals: [{ metric: JORNADA_EVENTS.focusBlock, target: 3 }],
      bonusXp: 20,
    },
  ],
};
